import React, { useCallback, useEffect, useState } from 'react';
import { EYEBROW, NOTE } from './typeScale';
import { grantAccess, readAccess, revokeAccess, ACCESS_LEVELS,
  APPROVE } from '../../services/protectBuild';

/**
 * Who may actually run this agent.
 *
 * <h3>Why an approver is not enough</h3>
 *
 * The readiness gate counts grants that are not approval duties: under
 * segregation of duties an approve grant decides a human-approval gate rather
 * than operating the agent. Grant only an approver and nobody can run it, and
 * the platform refuses Execution Ready. That has to be said at the moment it
 * happens, not discovered two steps later at a gate.
 *
 * <h3>Why a grant can be taken back here</h3>
 *
 * This is the step that hands out authority over an agent, and it had no way
 * to withdraw it — so a grant to the wrong person, or at the wrong level,
 * could only be undone by leaving for the full platform. Removing asks for the
 * same permission as granting, so anybody who could make the grant here can
 * undo it.
 *
 * <h3>Why the state is read back rather than counted here</h3>
 *
 * What is granted is the platform's answer, and the gate reads the same rows.
 * Counting grants locally would be a second opinion about whether the
 * requirement is met, and the two would part company the first time a grant
 * was made anywhere else.
 */
export default function AccessAsk({
  agentId, users = [], groups = [], error, onAnswer, busy = false,
}) {
  const [access, setAccess] = useState(null);
  const [subject, setSubject] = useState(null);
  const [level, setLevel] = useState('execute');
  const [working, setWorking] = useState(false);
  const [problem, setProblem] = useState(null);

  const refresh = useCallback(async () => {
    if (!agentId) return;
    setAccess(await readAccess(agentId));
  }, [agentId]);

  useEffect(() => { refresh(); }, [refresh]);

  const label = (a) => users.find((u) => u.id === a.userId)?.label
    || groups.find((g) => g.id === a.groupId)?.label
    || a.userId || a.groupId;

  /* Read back afterwards, never crossed off locally: the readiness gate counts
     the platform's own rows, and a second opinion here would part company with
     it the moment a grant changed anywhere else. */
  const revoke = async (a) => {
    setWorking(true); setProblem(null);
    try {
      await revokeAccess(agentId, a.id);
      await refresh();
    } catch (e) {
      setProblem(e?.message || 'The removal was refused.');
    } finally {
      setWorking(false);
    }
  };

  const grant = async () => {
    if (!subject) return;
    setWorking(true); setProblem(null);
    try {
      await grantAccess(agentId, subject.kind === 'user'
        ? { userId: subject.id, accessLevel: level }
        : { groupId: subject.id, accessLevel: level });
      setSubject(null);
      await refresh();
    } catch (e) {
      setProblem(e?.message || 'The grant was refused.');
    } finally {
      setWorking(false);
    }
  };

  const satisfied = access?.satisfied === true;
  const approversOnly = !satisfied && (access?.approvers?.length || 0) > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--p7-space-3)' }}>
      {error ? (
        <p className="p7-note p7-note--deny" style={{ margin: 0 }}>
          Could not read your team just now. You can grant access later.
        </p>
      ) : null}

      {access ? (
        <div data-testid="granted">
          {access.runtime.length === 0 && access.approvers.length === 0 ? (
            <p style={NOTE}>Nobody has access to this agent yet.</p>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex',
                         flexWrap: 'wrap', gap: '6px' }}>
              {access.runtime.map((a) => (
                <Granted key={a.id} tone="allow" name={label(a)} detail={a.accessLevel}
                         busy={busy || working} onRemove={() => revoke(a)} />
              ))}
              {access.approvers.map((a) => (
                <Granted key={a.id} tone="hold" name={label(a)} detail="approves only"
                         busy={busy || working} onRemove={() => revoke(a)} />
              ))}
            </ul>
          )}

          {/* Said before it is taken away, not after. Removing the last runtime
              grant leaves nobody able to run the agent and un-meets the gate,
              which is not something to discover from a line that changed. */}
          {access.runtime.length === 1 ? (
            <p className="p7-note p7-note--hold"
               style={{ margin: 'var(--p7-space-2) 0 0' }}>
              This is the only grant that lets anybody run it. Remove it and nobody can,
              and the readiness gate goes back to unmet.
            </p>
          ) : null}

          {/* The gate's own question, answered in its own terms. */}
          <p style={{ ...NOTE, marginTop: 'var(--p7-space-2)' }}>
            {satisfied
              ? 'Somebody can run it — the readiness gate is met on access.'
              : approversOnly
                ? 'Nobody can run it yet. An approve grant decides this agent’s approval '
                  + 'gates; it is not access, and the readiness gate does not count it.'
                : 'Until somebody can run it, the readiness gate reports access unmet.'}
          </p>
        </div>
      ) : null}

      {users.length || groups.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {[...users.map((u) => ({ ...u, kind: 'user' })),
              ...groups.map((g) => ({ ...g, kind: 'group' }))].map((s) => {
              const on = subject?.id === s.id && subject?.kind === s.kind;
              return (
                <button key={`${s.kind}-${s.id}`} type="button" aria-pressed={on}
                        onClick={() => setSubject(s)}
                        style={{
                          cursor: 'pointer', padding: '8px 12px',
                          borderRadius: 'var(--p7-radius-pill, 999px)',
                          border: `1px solid ${on ? 'var(--p7-accent)' : 'var(--p7-line-strong)'}`,
                          background: on ? 'var(--p7-accent-bg)' : 'var(--p7-surface)',
                          font: 'var(--p7-text-ui)', fontFamily: 'var(--p7-font-sans)',
                          color: 'var(--p7-ink)',
                        }}>
                  {s.label}
                  {s.kind === 'group' ? (
                    <span style={{ marginLeft: '6px', font: 'var(--p7-text-caption)',
                                   fontFamily: 'var(--p7-font-sans)',
                                   color: 'var(--p7-ink-subtle)' }}>group</span>
                  ) : null}
                </button>
              );
            })}
          </div>

          <label style={{ display: 'block', maxWidth: '420px' }}>
            <span style={{ display: 'block', marginBottom: '3px',
                           ...EYEBROW }}>
              Level
            </span>
            <select value={level} onChange={(e) => setLevel(e.target.value)}
                    style={{ width: '100%', padding: '9px 12px',
                             border: '1px solid var(--p7-line-strong)',
                             borderRadius: 'var(--p7-radius-control)',
                             font: 'var(--p7-text-body)', fontFamily: 'var(--p7-font-sans)',
                             background: 'var(--p7-surface)' }}>
              {ACCESS_LEVELS.map((l) => (
                <option key={l.value} value={l.value}>{l.label}</option>
              ))}
            </select>
          </label>

          {level === APPROVE ? (
            <p style={NOTE}>
              This names an approver for its human-approval gates. It is a duty, not access —
              somebody still has to be able to run it.
            </p>
          ) : null}
        </div>
      ) : null}

      {problem ? <p className="p7-note p7-note--deny" style={{ margin: 0 }}>{problem}</p> : null}

      <div style={{ display: 'flex', gap: '9px', alignItems: 'center', flexWrap: 'wrap' }}>
        {users.length || groups.length ? (
          <button type="button" className="p7-btn p7-btn--primary"
                  disabled={busy || working || !subject} onClick={grant}>
            {working ? 'Granting…' : 'Grant'}
          </button>
        ) : null}
        <button type="button" className="p7-btn p7-btn--sm" disabled={busy || working}
                onClick={() => onAnswer({ satisfied, granted: access?.runtime?.length || 0 })}>
          Continue
        </button>
        <button type="button" className="p7-btn p7-btn--sm" disabled={busy || working}
                onClick={() => onAnswer({ satisfied: false, deferred: true })}>
          Grant access later
        </button>
      </div>
    </div>
  );
}

/**
 * One grant, with the way to take it back.
 *
 * The control names who it removes, because "Remove" repeated down a list is
 * the same word on every row to anybody not looking at where they clicked.
 */
function Granted({ tone, name, detail, busy, onRemove }) {
  return (
    <li style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
      <span className={`p7-status p7-status--${tone}`}>
        {name} · {detail}
      </span>
      <button type="button" className="p7-btn p7-btn--sm p7-btn--ghost"
              aria-label={`Remove ${name}`} disabled={busy} onClick={onRemove}
              style={{ padding: '2px 7px' }}>
        Remove
      </button>
    </li>
  );
}

